import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useCallback } from "react";
import {
  getCurrentClient,
  createClient,
  updateClient,
  getClients,
  getClientByUserId,
  searchClients
} from "../api/clients-api";
import { 
  getFreelancerExpertises,
  getClientOnboardingTools,
  getBusinessDomains,
  getCommunicationChannels,
  getFreelancerFrequencies
} from "../api/reference-data-api";
import { 
  Client,
  ClientCreateRequest, 
  ClientUpdateRequest,
  FreelancerExpertise,
  Tool,
  CommunicationChannel,
  FreelancerFrequency
} from "../models/Client";
import { BusinessDomain } from "../models/BusinessDomain";
import { PaginatedResponse } from "../models/Pagination";
import { useLanguage } from "../i18n/LanguageContext";

/**
 * Hook to get the current user's client
 */
export const useClient = () => {
  return useQuery<Client | null, Error>({
    queryKey: ["client"],
    queryFn: getCurrentClient,
    retry: 1, // Only retry once
  });
};

/**
 * Hook to get a paginated list of clients with language support
 * Only accessible to Admin and CSM
 */
export const useClients = (page = 1, pageSize = 10, sortField?: string, sortDirection?: string) => {
  const { currentLanguage } = useLanguage();

  return useQuery<PaginatedResponse<Client>, Error>({
    queryKey: ["clients", page, pageSize, sortField, sortDirection, currentLanguage],
    queryFn: () => getClients(page, pageSize, sortField, sortDirection),
  });
};

/**
 * Hook to get a client by user ID
 */
export const useClientByUserId = (userId: number) => {
  const { currentLanguage } = useLanguage();

  return useQuery<Client, Error>({
    queryKey: ["client", userId, currentLanguage],
    queryFn: () => getClientByUserId(userId),
    enabled: userId !== 0,
  });
};

/**
 * Hook to create a client
 */
export const useCreateClient = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (clientData: ClientCreateRequest) => 
      createClient(clientData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["client"] });
      queryClient.invalidateQueries({ queryKey: ["clients"] });
    }
  });
};

/**
 * Hook to update a client
 */
export const useUpdateClient = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ clientData, userId }: { clientData: ClientUpdateRequest; userId?: number }) => 
      updateClient(clientData, userId),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: ["client"] });
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      if (userId) {
        queryClient.invalidateQueries({ queryKey: ["client", userId] });
      }
    }
  });
};

/**
 * Hook to get freelancer expertise options
 */
export const useFreelancerExpertises = () => {
  return useQuery<FreelancerExpertise[], Error>({
    queryKey: ["freelancerExpertises"],
    queryFn: getFreelancerExpertises
  });
};

/**
 * Hook to get client onboarding tools
 */
export const useClientOnboardingTools = () => {
  return useQuery<Tool[], Error>({
    queryKey: ["clientOnboardingTools"],
    queryFn: getClientOnboardingTools
  });
};

/**
 * Hook to get business domain options
 */
export const useBusinessDomains = () => {
  return useQuery<BusinessDomain[], Error>({
    queryKey: ["businessDomains"],
    queryFn: getBusinessDomains
  });
};

/**
 * Hook to get communication channel options
 */
export const useCommunicationChannels = () => {
  return useQuery<CommunicationChannel[], Error>({
    queryKey: ["communicationChannels"],
    queryFn: getCommunicationChannels
  });
};

/**
 * Hook to get freelancer frequency options
 */
export const useFreelancerFrequencies = () => {
  return useQuery<FreelancerFrequency[], Error>({
    queryKey: ["freelancerFrequencies"],
    queryFn: getFreelancerFrequencies
  });
};

/**
 * Hook to search clients with server-side pagination
 * Only accessible to Admin and CSM
 */
export const useSearchClients = () => {
  const [data, setData] = useState<PaginatedResponse<Client> | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isSearched, setIsSearched] = useState(false);

  const search = useCallback(async (
    searchTerm: string,
    page: number = 1,
    pageSize: number = 10,
    onboardingCompleted?: boolean,
    companyId?: number,
    sortField?: string,
    sortDirection?: string,
    hasMatchings?: boolean
  ) => {
    // Require at least 2 characters or a filter
    if (searchTerm.trim().length < 2 &&
        onboardingCompleted === undefined &&
        companyId === undefined &&
        hasMatchings === undefined) {
      return;
    }

    setIsLoading(true);
    setError(null);
    setIsSearched(true);

    try {
      const result = await searchClients(
        searchTerm,
        page,
        pageSize,
        onboardingCompleted,
        companyId,
        sortField,
        sortDirection,
        hasMatchings
      );
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Search failed"));
    } finally {
      setIsLoading(false);
    }
  }, []);

  const resetSearch = useCallback(() => {
    setData(null);
    setIsSearched(false);
    setError(null);
  }, []);

  return { data, isLoading, error, isSearched, search, resetSearch };
};